import { BRANCH_DIMENSION_REPORT_TEMPLATE_IDS } from "@ibada/shared-types/reporting"

import {
  FinanceReportCategory,
  ReportFieldKind,
  type FinanceReportCategoryMeta,
  type FinanceReportEntry,
} from "@/features/reports/model/report-registry-types"
import { ACCOUNTING_ENTRIES } from "@/features/reports/registry/accounting-entries"
import {
  CONSOLIDATED_ENGINE_ENTRIES,
  ENGINE_ENTRIES,
  PARISH_ACCOUNTING_ENTRIES,
} from "@/features/reports/registry/engine-entries"
import { FINANCE_RESOURCE_ENTRIES } from "@/features/reports/registry/finance-resource-entries"
import { MEMBER_ENTRIES } from "@/features/reports/registry/people-entries"
import { ACTIVITY_ENTRIES } from "@/features/reports/registry/activity-entries"

/**
 * The full report catalog, across every module.
 *
 * Aggregated from one file per source so each stays readable, and a new MODULE arrives the same way the
 * member and activity ones did: a curation file and a line here. Nothing about the page changes.
 */
export const FINANCE_REPORTS: readonly FinanceReportEntry[] = [
  ...ACCOUNTING_ENTRIES,
  ...ENGINE_ENTRIES,
  ...FINANCE_RESOURCE_ENTRIES,
  ...MEMBER_ENTRIES,
  ...ACTIVITY_ENTRIES,
]

const REPORT_BY_ID: ReadonlyMap<string, FinanceReportEntry> = new Map(
  FINANCE_REPORTS.map((entry) => [entry.id, entry]),
)
const GLOBAL_REPORT_BY_ID: ReadonlyMap<string, FinanceReportEntry> = new Map(
  [...CONSOLIDATED_ENGINE_ENTRIES, ...PARISH_ACCOUNTING_ENTRIES].map((entry) => [entry.id, entry]),
)

/**
 * Reports whose rows are branches only make sense on the consolidated parish/global surface.
 *
 * On a mono-branch or one branch's Finance → Reports catalog they collapse to a single-row answer while still
 * wearing a "by branch" name. That is the broken card the screenshots show.
 */
export const PARISH_ONLY_BRANCH_COMPARISON_REPORT_IDS: ReadonlySet<string> = new Set(
  BRANCH_DIMENSION_REPORT_TEMPLATE_IDS,
)

/**
 * Budgeting is currently paused in the tenant routes, so report entries that depend on budgets must be hidden
 * from the user-facing catalog too. The underlying registry entries stay defined so they can be re-enabled
 * deliberately when the product unpauses budgets.
 */
export const PAUSED_BUDGET_REPORT_IDS: ReadonlySet<string> = new Set(
  FINANCE_REPORTS.filter((entry) => entry.category === FinanceReportCategory.Budgets).map((entry) => entry.id),
)

export function isPausedBudgetReport(entry: FinanceReportEntry): boolean {
  return PAUSED_BUDGET_REPORT_IDS.has(entry.id)
}

export function isParishOnlyBranchComparisonReport(entry: FinanceReportEntry): boolean {
  return PARISH_ONLY_BRANCH_COMPARISON_REPORT_IDS.has(entry.id)
}

export enum FinanceReportCatalogContext {
  /** A single church: no parish/branch split exists in the UI. */
  MonoBranch = "mono_branch",
  /** A normal branch inside a multi-branch workspace. */
  MultiBranchBranch = "multi_branch_branch",
  /** Multi-branch parish/global finance where the parish can read every branch's books in full. */
  MultiBranchParishFullGovernance = "multi_branch_parish_full_governance",
  /** Multi-branch parish/global finance where branches keep autonomous books. */
  MultiBranchParishBranchAutonomy = "multi_branch_parish_branch_autonomy",
}

const BRANCH_CATALOG_REPORT_IDS: readonly string[] = [
  "balance-sheet",
  "income-statement",
  "cash-flow",
  "trial-balance",
  "general-ledger",
  "journal-entries",
  "chart-of-accounts",
  "income-vs-expense-trend",
  "expense-by-category",
  "income-by-category",
  "income-category-trend",
  "contribution-by-group",
  "contribution-by-ministry",
  "top-contributors",
  "pledge-fulfillment-by-activity",
  "pledge-fulfillment-by-group",
  "pledge-fulfillment-by-ministry",
  "unfulfilled-pledges-by-member",
  "fully-fulfilled-pledges-by-member",
  "members-with-multiple-pledges",
  "pledges-without-fulfillment",
  "top-pledge-contributors",
  "pledges-by-contributor-type",
  "outstanding-pledges-by-activity",
  "pledges-promised-vs-fulfilled-by-month",
  "finance-by-activity",
  "activities-by-revenue",
  "activities-by-expense",
  "reconciliation-summary",
  "fund-utilization",
  "fund-activity",
  "department-summary",
  "members-by-status",
  "new-members-by-month",
  "members-by-gender",
  "members-by-group",
  "members-by-ministry",
  "group-membership",
  "ministry-membership",
  "members-without-group",
  "activity-participation",
  "activities-by-ministry",
]

const PARISH_FULL_GOVERNANCE_CATALOG_REPORT_IDS: readonly string[] = [
  "income-vs-expense-trend",
  "net-balance-by-branch",
  "income-by-branch",
  "expense-by-branch",
  "income-by-category",
  "expense-by-category",
  "income-category-trend",
  "pledge-fulfillment-by-branch",
  "pledge-fulfillment-by-activity",
  "pledges-by-contributor-type",
  "pledges-promised-vs-fulfilled-by-month",
  "outstanding-pledges-by-activity",
  "finance-by-activity",
  "activities-by-revenue",
  "activities-by-expense",
  "balance-sheet",
  "income-statement",
  "cash-flow",
  "trial-balance",
  "general-ledger",
  "journal-entries",
]

const PARISH_BRANCH_AUTONOMY_CATALOG_REPORT_IDS: readonly string[] = [
  "income-vs-expense-trend",
  "income-by-category",
  "expense-by-category",
  "income-category-trend",
  "pledge-fulfillment-by-activity",
  "pledges-by-contributor-type",
  "pledges-promised-vs-fulfilled-by-month",
  "outstanding-pledges-by-activity",
  "finance-by-activity",
  "activities-by-revenue",
  "activities-by-expense",
  "balance-sheet",
  "income-statement",
  "cash-flow",
  "trial-balance",
  "general-ledger",
  "journal-entries",
]

export const FINANCE_REPORT_CATALOG_TEMPLATE_IDS = {
  [FinanceReportCatalogContext.MonoBranch]: BRANCH_CATALOG_REPORT_IDS,
  [FinanceReportCatalogContext.MultiBranchBranch]: BRANCH_CATALOG_REPORT_IDS,
  [FinanceReportCatalogContext.MultiBranchParishFullGovernance]: PARISH_FULL_GOVERNANCE_CATALOG_REPORT_IDS,
  [FinanceReportCatalogContext.MultiBranchParishBranchAutonomy]: PARISH_BRANCH_AUTONOMY_CATALOG_REPORT_IDS,
} satisfies Record<FinanceReportCatalogContext, readonly string[]>

function entriesForIds(
  context: FinanceReportCatalogContext,
  source: ReadonlyMap<string, FinanceReportEntry>,
): FinanceReportEntry[] {
  return FINANCE_REPORT_CATALOG_TEMPLATE_IDS[context].map((id) => {
    const entry = source.get(id)
    if (!entry) {
      throw new Error(`Report catalog context "${context}" references unknown report "${id}"`)
    }
    return entry
  })
}

/**
 * The product-facing report catalog registry.
 *
 * Do not render `FINANCE_REPORTS` directly. That is the complete internal definition list; this registry is
 * the deny-by-default contract for what each tenant context may actually offer to users.
 */
export const FINANCE_REPORT_CATALOG_REGISTRY: Readonly<
  Record<FinanceReportCatalogContext, readonly FinanceReportEntry[]>
> = {
  [FinanceReportCatalogContext.MonoBranch]: entriesForIds(
    FinanceReportCatalogContext.MonoBranch,
    REPORT_BY_ID,
  ),
  [FinanceReportCatalogContext.MultiBranchBranch]: entriesForIds(
    FinanceReportCatalogContext.MultiBranchBranch,
    REPORT_BY_ID,
  ),
  [FinanceReportCatalogContext.MultiBranchParishFullGovernance]: entriesForIds(
    FinanceReportCatalogContext.MultiBranchParishFullGovernance,
    GLOBAL_REPORT_BY_ID,
  ),
  [FinanceReportCatalogContext.MultiBranchParishBranchAutonomy]: entriesForIds(
    FinanceReportCatalogContext.MultiBranchParishBranchAutonomy,
    GLOBAL_REPORT_BY_ID,
  ),
}

export function getFinanceReportsForCatalogContext(
  context: FinanceReportCatalogContext,
): readonly FinanceReportEntry[] {
  return FINANCE_REPORT_CATALOG_REGISTRY[context]
}

/** Ordered categories for the catalog layout (empty categories are simply not rendered). */
export const CATEGORY_ORDER: readonly FinanceReportCategoryMeta[] = [
  { category: FinanceReportCategory.FinancialStatements, labelKey: "finance.reports.categories.financialStatements" },
  { category: FinanceReportCategory.Transactions, labelKey: "finance.reports.categories.transactions" },
  { category: FinanceReportCategory.Funds, labelKey: "finance.reports.categories.funds" },
  { category: FinanceReportCategory.Budgets, labelKey: "finance.reports.categories.budgets" },
  { category: FinanceReportCategory.Activities, labelKey: "finance.reports.categories.activities" },
  { category: FinanceReportCategory.Giving, labelKey: "finance.reports.categories.giving" },
  { category: FinanceReportCategory.Assets, labelKey: "finance.reports.categories.assets" },
  { category: FinanceReportCategory.Audit, labelKey: "finance.reports.categories.audit" },
  // Sections belonging to the modules beyond finance.
  { category: FinanceReportCategory.Membership, labelKey: "finance.reports.categories.membership" },
  { category: FinanceReportCategory.Directory, labelKey: "finance.reports.categories.directory" },
  { category: FinanceReportCategory.Participation, labelKey: "finance.reports.categories.participation" },
]

export function getReportById(id: string): FinanceReportEntry | undefined {
  return REPORT_BY_ID.get(id)
}

export function getReportsByCategory(category: FinanceReportCategory): FinanceReportEntry[] {
  return FINANCE_REPORTS.filter((entry) => entry.category === category)
}

/** Keyword/id match (literal). Title/description search is done at render time with resolved i18n text. */
export function matchesKeyword(entry: FinanceReportEntry, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  if (entry.id.includes(q)) return true
  return entry.keywords.some((keyword) => keyword.toLowerCase().includes(q))
}

/** True when a report needs no required config → the catalog can "Generate" straight to the viewer. */
export function hasRequiredConfig(entry: FinanceReportEntry): boolean {
  return Boolean(entry.configSchema?.fields.some((field) => field.required))
}

/**
 * A report is configurable/saveable only when it is scoped to a specific ENTITY (an account, fund, activity…) —
 * a required field that is not merely a time period. A date range alone does not define a distinct, bookmarkable
 * report, so period-only reports (balance sheet, income statement, …) get NO Configure/Save affordance.
 */
export function isConfigurableReport(entry: FinanceReportEntry): boolean {
  return Boolean(
    entry.configSchema?.fields.some((field) => field.required && field.kind !== ReportFieldKind.Period),
  )
}
